import React from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useForm, Controller } from "react-hook-form";

/**
 * The clinician's own diagnosis: asked for at the top of the Diagnosis step, before anything
 * the model produced is on screen, and editable afterwards from the same form.
 *
 * ⚠️ THE ASKING IS THE FEATURE, AND THE SCREEN NEVER SAYS SO. A step that explains it is
 * withholding a differential to stop the clinician leaning on it has told them there is a
 * differential to lean on, and made the encounter about the software. It asks for a working
 * diagnosis the way every other step asks for what it needs, and that is all.
 *
 * ⚠️ NO PLACEHOLDER IN THE DIAGNOSIS FIELD. An example condition is a suggestion, and this is the
 * one field on the flow where the software must not put a condition in front of the clinician.
 * It also reads as a hint about this patient when it is nothing of the kind.
 */

const FIELD_LABELS = {
  condition: "Working diagnosis",
  reasoning: "Supporting findings (optional)",
};

const useDiagnosisForm = (impression) =>
  useForm({
    defaultValues: {
      condition: impression?.condition || "",
      reasoning: impression?.reasoning || "",
    },
    mode: "onSubmit",
  });

const DiagnosisFields = ({ control, errors, autoFocus = false }) => (
  <>
    <Controller
      name="condition"
      control={control}
      rules={{
        validate: (value) =>
          value.trim().length > 0 || "Please enter a working diagnosis.",
      }}
      render={({ field }) => (
        <TextField
          {...field}
          autoFocus={autoFocus}
          fullWidth
          label={FIELD_LABELS.condition}
          error={Boolean(errors.condition)}
          helperText={errors.condition?.message || " "}
        />
      )}
    />

    <Controller
      name="reasoning"
      control={control}
      render={({ field }) => (
        <TextField
          {...field}
          fullWidth
          multiline
          minRows={2}
          label={FIELD_LABELS.reasoning}
        />
      )}
    />
  </>
);

/**
 * The Diagnosis step until the clinician has answered.
 *
 * `analysisRunning` is the differential being built behind this screen. It is shown as a bare
 * spinner with no copy, because there is nothing to wait for and nothing to do about it.
 */
export const ClinicianDiagnosisGate = ({ analysisRunning = false, onSubmit }) => {
  const {
    control,
    handleSubmit,
    getValues,
    watch,
    formState: { errors },
  } = useDiagnosisForm(null);

  const condition = watch("condition");

  const submit = ({ condition: value, reasoning }) =>
    onSubmit?.({
      condition: value.trim(),
      reasoning: reasoning.trim(),
      undetermined: false,
    });

  const submitUndetermined = () =>
    onSubmit?.({
      condition: "",
      reasoning: getValues("reasoning").trim(),
      undetermined: true,
    });

  return (
    <Box sx={{ width: "100%" }}>
      <Box sx={{ textAlign: "center", mb: 4 }}>
        <Typography variant="h4" sx={{ mb: { xs: 1.5, sm: 2 } }}>
          Initial Impression
        </Typography>
        {/* A question rather than an instruction, because the answer "I'm not sure yet" has to
            be an answer to something. Told to record a diagnosis, a clinician who does not have
            one yet reads that button as a way out of the step. */}
        <Typography variant="body1" sx={{ color: "text.secondary", lineHeight: 1.6 }}>
          What is your working diagnosis for this encounter?
        </Typography>
      </Box>

      <Box component="form" onSubmit={handleSubmit(submit)} noValidate>
        <DiagnosisFields control={control} errors={errors} autoFocus />

        {/* Both answers sit together at the end of the form. Pushed to the far side of the row,
            "I'm not sure yet" reads as a way out of the step rather than as the other answer to
            the question, and gets missed by the clinician it is there for. */}
        <Stack
          direction={{ xs: "column-reverse", sm: "row" }}
          spacing={1.5}
          sx={{ mt: 3, alignItems: { sm: "center" }, justifyContent: "flex-end" }}
        >
          {analysisRunning && <CircularProgress size={20} />}
          <Button onClick={submitUndetermined}>I'm not sure yet</Button>
          <Button type="submit" variant="contained" disabled={!condition.trim()}>
            Continue
          </Button>
        </Stack>
      </Box>
    </Box>
  );
};

ClinicianDiagnosisGate.propTypes = {
  analysisRunning: PropTypes.bool,
  onSubmit: PropTypes.func.isRequired,
};

/**
 * Editing it afterwards, from the Diagnosis step.
 *
 * Revising a working diagnosis in the light of the differential is ordinary clinical practice,
 * and the encounter has to be able to carry the diagnosis the clinician actually settled on. The
 * first answer is not lost by editing — the session keeps it — but that is a record, not
 * something this dialog argues with the clinician about.
 */
export const ClinicianDiagnosisDialog = ({ open, impression, onClose, onSubmit }) => {
  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useDiagnosisForm(impression);

  // The dialog stays mounted between opens, so its fields are re-seeded from whatever the
  // session holds each time it is opened rather than keeping the last edit's text.
  React.useEffect(() => {
    if (open) {
      reset({
        condition: impression?.condition || "",
        reasoning: impression?.reasoning || "",
      });
    }
  }, [open, impression, reset]);

  const condition = watch("condition");

  const submit = ({ condition: value, reasoning }) => {
    onSubmit?.({
      condition: value.trim(),
      reasoning: reasoning.trim(),
      undetermined: false,
    });
    onClose?.();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Initial Impression</DialogTitle>
      <Box component="form" onSubmit={handleSubmit(submit)} noValidate>
        <DialogContent>
          <DiagnosisFields control={control} errors={errors} autoFocus />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={!condition.trim()}>
            Save
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

ClinicianDiagnosisDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  impression: PropTypes.shape({
    condition: PropTypes.string,
    reasoning: PropTypes.string,
  }),
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

export default ClinicianDiagnosisGate;
